/**
 * Bot Detection Utilities
 * 
 * Provides conservative bot detection for handling navigation
 * and preventing unnecessary redirects for crawlers.
 */

/**
 * Detects if the current user agent is an obvious bot/crawler
 * Uses conservative detection - only flags obvious bots
 * 
 * @returns {boolean} True if definitely a bot, false otherwise
 */
export function isDefiniteBot() {
  if (typeof window === 'undefined') return true;
  
  const userAgent = navigator.userAgent?.toLowerCase() || '';
  
  // Only detect OBVIOUS bots - be very conservative
  const obviousBots = [
    'googlebot', 'bingbot', 'slurp', 'duckduckbot', 'baiduspider',
    'yandexbot', 'facebookexternalhit', 'twitterbot', 'linkedinbot',
    'whatsapp', 'telegrambot', 'crawler', 'spider', 'indexer'
  ];
  
  // Only flag if it's a clear, unambiguous bot
  const isObviousBot = obviousBots.some(bot => userAgent.includes(bot));
  if (isObviousBot) {
    console.log('Obvious bot detected via user agent:', userAgent);
    return true;
  }
  
  // Check for headless automation tools (but be more specific)
  const hasAutomationFlags = !!(
    navigator.webdriver ||
    window.phantom ||
    window.callPhantom ||
    userAgent.includes('headlesschrome') ||
    userAgent.includes('phantomjs') ||
    userAgent.includes('selenium')
  );
  
  if (hasAutomationFlags) {
    console.log('Automation tools detected:', userAgent);
    return true;
  }
  
  return false;
}

/**
 * Tracks user interaction to distinguish from bots
 * Sets a session flag when user interacts with the page
 */
export function setupInteractionTracking() {
  const trackInteraction = () => {
    sessionStorage.setItem('has_user_interaction', 'true');
  };
  
  // Listen for ANY user interaction
  ['mousedown', 'keydown', 'touchstart', 'scroll'].forEach(event => {
    document.addEventListener(event, trackInteraction, { once: true, passive: true });
  });
  
  // Return cleanup function
  return () => {
    ['mousedown', 'keydown', 'touchstart', 'scroll'].forEach(event => {
      document.removeEventListener(event, trackInteraction);
    });
  };
}

/**
 * Checks if user has interacted with the page
 * 
 * @returns {boolean} True if user has interacted
 */
export function hasUserInteraction() {
  return sessionStorage.getItem('has_user_interaction') === 'true';
}

